import React, { ReactNode, useMemo, useState } from "react";
import { DatePicker, DateTimePicker } from "@mui/x-date-pickers";
import { MenuItem, Select, Box, SelectChangeEvent } from "@mui/material";
import { useTranslation } from 'react-i18next';
import dayjs from "dayjs";
import utc from 'dayjs/plugin/utc';

import RelativeDateSelector from "./RelativeDateSelector";
import { SimplifiedRelativeDateSelector } from "./SimplifiedRelativeDateSelector";

// Add utc timezone support to dayjs
dayjs.extend(utc);

export enum DateSelectorType {
  Date = "date",
  DateTime = "dateTime",
  Relative = "relative",
  Now = "now"
}

interface DateSelectorOption {
  type: DateSelectorType;
  label: string;
}

interface QueryDateSelectorProps {
  exclude?: DateSelectorType[];
  disabled?: boolean;
  onChange(value: string): void;
  value: string | null;
  minDate?: dayjs.Dayjs | undefined;

  includeNowOption?: boolean;

  disableRelativeDates?: boolean;

  defaultDateValue?: string;
  useSimpifiedRelativeDates?: boolean;
  /**
   * Determines if the selected date is inclusive of the selected date (starts at the beginning of the day).
   * @default false
   */
  isDateInclusive?: boolean;
}

function getSelectorTypeByValue(value: string): DateSelectorType {
  if (value === "now") {
    return DateSelectorType.Now;
  }
  if (value.startsWith("-") || value.startsWith("P")) {
    return DateSelectorType.Relative;
  }
  if (value.includes("T") && !value.includes("T00:00:00") && !value.includes("T23:59:59")) {
    return DateSelectorType.DateTime;
  }
  return DateSelectorType.Date;
}

export function QueryDateSelector(props: QueryDateSelectorProps): React.JSX.Element {
  const i18n  = useTranslation();

  // If a value is sent, it will be a string type.
  const initialDateTime = props.value ? props.value as string : (props.defaultDateValue ? props.defaultDateValue : null);
  const initialSelectorType = getSelectorTypeByValue(initialDateTime ?? "");

  const [selectorType, setSelectorType] = useState<DateSelectorType>(initialSelectorType);

  const selectorOptions = useMemo((): DateSelectorOption[] => {
    const sOpts = [
      { type: DateSelectorType.Date, label: i18n.t("date") },
      { type: DateSelectorType.DateTime, label: i18n.t("date-time") }
    ]
    if (!props.disableRelativeDates) {
      sOpts.push({ type: DateSelectorType.Relative, label: i18n.t("relative") });
    }
    if (props.includeNowOption) {
      sOpts.push({ type: DateSelectorType.Now, label: i18n.t("now") });
    }
    return sOpts;
  }, [props.includeNowOption]);

  const options = selectorOptions.reduce((result: DateSelectorOption[], current: DateSelectorOption) => {
    if (!props.exclude || !props.exclude.includes(current.type)) {
      result.push(current);
    }
    return result;
  }, []);

  let dayjsDay = null;
  if (initialDateTime) {
    dayjsDay = dayjs(initialDateTime);
  }

  return (
    <>
      <Select
        disabled={props.disabled ?? false}
        value={selectorType}
        sx={{ marginRight: "10px", verticalAlign: "middle" }}
        onChange={(event: SelectChangeEvent<DateSelectorType>) => {
          setSelectorType(event.target.value as DateSelectorType);
          if (event.target.value === DateSelectorType.Now) {
            props.onChange(event.target.value);
          }
        }} >
        {options.map((option: DateSelectorOption): ReactNode => {
          return <MenuItem key={option.type} value={option.type}>{option.label}</MenuItem>;
        })}
      </Select>
      {selectorType === DateSelectorType.Date &&
        <DatePicker
          minDate={props.minDate}
          sx={{ verticalAlign: "middle" }}
          disabled={props.disabled ?? false}
          label={i18n.t("date")}
          value={dayjsDay}
          timezone="UTC"
          onChange={(newValue) => {
            const newDate = dayjs.utc(newValue);
            if (newDate.isValid()) {
              const isoDateTime = props.isDateInclusive ? newDate.toISOString() : newDate.endOf("day").toISOString();
              props.onChange(isoDateTime);
            }
          }}
        />
      }
      {selectorType === DateSelectorType.DateTime &&
        <DateTimePicker
          minDate={props.minDate}
          sx={{ verticalAlign: "middle" }}
          disabled={props.disabled ?? false}
          label={i18n.t("date-time")}
          value={dayjsDay}
          timezone="UTC"
          onChange={(newValue) => {
            const newDate = dayjs.utc(newValue);
            if (newDate.isValid()) {
              const isoDateTime = newDate.toISOString();
              props.onChange(isoDateTime);
            }
          }}
        />
      }
      {(selectorType === DateSelectorType.Relative && !props.useSimpifiedRelativeDates) &&
        <Box sx={{ margin: "10px 0" }}>
          <RelativeDateSelector
            disabled={props.disabled}
            relativeDate={props.value as string}
            setRelativeDate={(newValue: string) => {
              props.onChange(newValue);
            }}
          />
        </Box>
      }
      {(selectorType === DateSelectorType.Relative && props.useSimpifiedRelativeDates) &&
        <Box sx={{ margin: "0", display: "inline-block" }}>
          <SimplifiedRelativeDateSelector
            minDate={props.minDate}
            relativeDate={props.value as string}
            setRelativeDate={props.onChange}
          />
        </Box>
      }
    </>
  );
}
